import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { useEffectOnce } from 'react-use';
import search from '~/assets/icons/search.svg';
import { ShopListWithScroll } from '~/components/ui/ShopListWithScroll';
import { Text } from '~/components/ui/Text';
import { rage, useInputAction, useObservable } from '~/rage';
import { Gender, InputAction, WalletType } from '~/shared';
import { useBuy } from '../../hooks';
import {
	setActiveItemId,
	setActiveList,
	setActiveListIndex,
	setActiveStage,
	setMaxListIndex,
	setSearchValue,
	StageTypes,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { CategoryImages, CategoryNames } from '../CategoryTab';
import { ListTabItem } from '../ListTabItem';
import { groupItems } from './ListTab.model';
import { StyledEmptyContainer } from './ListTab.styled';

const ListTab = ({
	activeVariants,
	onVariantChange,
	resetVariants,
}: {
	activeVariants: Record<string, number>;
	onVariantChange: (itemId: string, newIndex: number) => void;
	resetVariants: () => void;
}) => {
	const dispatch = useLayoutDispatch();
	const activeListIndex = useLayoutSelector(
		(state) => state.clothesShop.activeListIndex,
	);
	const maxListIndex = useLayoutSelector(
		(state) => state.clothesShop.maxListIndex,
	);
	const activeCategory = useLayoutSelector(
		(state) => state.clothesShop.activeCategory,
	);
	const tabIndex = useLayoutSelector((state) => state.clothesShop.tabIndex);
	const shopItems = useLayoutSelector((state) => state.clothesShop.shopItems);
	const ownedItems = useLayoutSelector((state) => state.clothesShop.ownedItems);
	const searchValue = useLayoutSelector(
		(state) => state.clothesShop.searchValue,
	);
	const playerGender = useObservable(rage.player.getPlayerGender());
	const { handleBuy } = useBuy(activeVariants);

	const items = useMemo(() => {
		const hasSearchValue = searchValue.trim().length > 0;

		if (tabIndex === 0) {
			if (hasSearchValue) {
				return groupItems(
					shopItems
						.filter(
							(item) =>
								item.name.toLowerCase().includes(searchValue.toLowerCase()) &&
								(item.gender === playerGender || item.gender === Gender.COMMON),
						)
						.map((item) => ({
							...item,
							active: false,
							new: false,
						})),
				);
			}
			return groupItems(
				shopItems
					.filter(
						(item) =>
							item.category === activeCategory &&
							(item.gender === playerGender || item.gender === Gender.COMMON),
					)
					.map((item) => ({
						...item,
						active: false,
						new: false,
					})),
			);
		}

		return groupItems(
			ownedItems
				.filter(
					(item) =>
						item.category === activeCategory &&
						(item.gender === playerGender || item.gender === Gender.COMMON),
				)
				.map((item) => ({
					...item,
					active: false,
					new: false,
				})),
		);
	}, [
		tabIndex,
		shopItems,
		ownedItems,
		activeCategory,
		searchValue,
		playerGender,
	]);

	const categoryInfo = useMemo(() => {
		if (
			searchValue.trim().length > 0 ||
			(!activeCategory && activeCategory !== 0)
		) {
			return {
				icon: search,
				title: 'Поиск',
			};
		}

		return {
			icon: CategoryImages[activeCategory],
			title: CategoryNames[activeCategory],
		};
	}, [searchValue, activeCategory]);

	const handleSelectItem = useCallback((index: number | null) => {
		if (index === null) {
			return;
		}

		dispatch(setActiveListIndex(index));
	}, []);

	const handleBack = useCallback(() => {
		dispatch(setActiveStage(StageTypes.Category));
	}, []);

	const getItemId = useCallback(
		(index: number) => {
			const item = items[index];
			return item ? `clothes-${item.id}` : '';
		},
		[items],
	);

	useInputAction(InputAction.CommonMenuAccept, () =>
		handleBuy(WalletType.Cash),
	);

	useEffect(() => {
		dispatch(setMaxListIndex(items.length - 1));
		dispatch(setActiveList(items));
	}, [items]);

	useEffect(() => {
		if (
			items.length > 0 &&
			(activeListIndex === null || activeListIndex >= items.length)
		) {
			dispatch(setActiveListIndex(0));
		} else if (items.length === 0 && activeListIndex !== null) {
			dispatch(setActiveListIndex(null));
		}
	}, [items.length, activeListIndex]);

	useEffectOnce(() => {
		return () => {
			rage.clothesShop
				.clearPreview()
				.catch((err) => rage.notifications.showException(err));
			resetVariants();
			dispatch(setActiveItemId(''));
			dispatch(setActiveListIndex(null));
		};
	});

	useEffectOnce(() => {
		return () => {
			dispatch(setActiveListIndex(null));
			dispatch(setSearchValue(''));
		};
	});

	return (
		<Fragment>
			<ShopListWithScroll
				icon={categoryInfo.icon}
				title={categoryInfo.title}
				style={{ marginTop: 30 }}
				handleBack={handleBack}
				activeIndex={activeListIndex}
				maxIndex={maxListIndex}
				handleCheckInstall={() => false}
				handleSelect={handleSelectItem}
				enableAutoScroll={true}
				getItemId={getItemId}
			>
				{items.length > 0 ? (
					items.map((item, index) => (
						<ListTabItem
							item={item}
							key={index}
							index={index}
							handleSelect={handleSelectItem}
							variationIndex={activeVariants[item.id] || 0}
							onVariationChange={(newIndex) =>
								onVariantChange(item.id, newIndex)
							}
							category={CategoryNames[item.items[0].category]}
						/>
					))
				) : (
					<StyledEmptyContainer>
						<Text style={{ opacity: 0.5, fontSize: 16 }}>
							Ничего не найдено
						</Text>
					</StyledEmptyContainer>
				)}
			</ShopListWithScroll>
		</Fragment>
	);
};

export { ListTab };
